/**
 * The rules behind the overview's bands, tiles and filters (masterplan credit-limits-v3, A7; E30, E31, E16). Pure, so
 * the tests read them without a browser; `OverviewTab` and the cells in `overviewColumns.tsx` only draw what this file
 * decides.
 *
 * - Bands (E30): a ratio above 1 is «over» (red), from NearLimitPct/100 up to and including 1 «near» (amber), below
 *   «ok» (green); a customer without a limit (limit <= 1, ratio null) is «none» (grey). Utilization and preview use
 *   the same bands; the row takes the worse of the two.
 * - Tiles count the rows the page filters leave (scope, search, salesperson, «My customers»), never the tile's own
 *   filter: a pressed tile keeps the other counts readable. The grid's funnels stay inside the grid and are not counted.
 * - «My customers» (E31): a salesperson sees the customers whose `SalesManagerName` is one of `smFor`, a BT manager
 *   additionally those of `btmFor`, finance all. Names are compared exactly, as `canWriteComments` does: the approver
 *   rows carry the same `SalesManagerName` values as the overview (checked against DolderDB 2026-10-23).
 * - Search: every word must occur in the customer name, number, salesperson or payment terms; case and umlauts do not
 *   matter («seegmuller», «seegmueller» and «Seegmüller» find each other).
 * - The filter state lives in the URL (`?q=`, `?sm=`, `?mine=1`, `?band=`) so a link reproduces the list.
 */
import type { CreditLimitCallerRoles, LimitBand, OverviewRow } from '../../../types/creditLimits';

// ---------------------------------------------------------------- bands (E30)

const BAND_RANK: Record<LimitBand, number> = { none: 0, ok: 1, near: 2, over: 3 };

/** The band of one ratio. No limit (`hasLimit` false, limit <= 1) or a missing ratio is «none». */
export function limitBand(ratio: number | null | undefined, row: Pick<OverviewRow, 'hasLimit' | 'creditLimitChf'>, nearLimitPct: number): LimitBand {
    if (!row.hasLimit || row.creditLimitChf === null || !(row.creditLimitChf > 1)) return 'none';
    if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return 'none';
    if (ratio > 1) return 'over';
    if (ratio >= nearLimitPct / 100) return 'near';
    return 'ok';
}

export const utilizationBand = (row: OverviewRow, nearLimitPct: number): LimitBand => limitBand(row.utilization, row, nearLimitPct);
export const previewBand = (row: OverviewRow, nearLimitPct: number): LimitBand => limitBand(row.preview, row, nearLimitPct);

/** The worse of the two bands: the row's tint. */
export function rowBand(row: OverviewRow, nearLimitPct: number): LimitBand {
    const u = utilizationBand(row, nearLimitPct);
    const p = previewBand(row, nearLimitPct);
    return BAND_RANK[p] > BAND_RANK[u] ? p : u;
}

/** Words for a band, so it never rests on colour alone (screen readers, colour blindness, the tooltip). */
export const BAND_TEXT: Record<LimitBand, string> = {
    over: 'Over limit',
    near: 'Near limit',
    ok: 'Within limit',
    none: 'No limit',
};

/** The row's class: a subtle tint for red and amber; green and «no limit» stay plain. */
export function rowBandClass(row: OverviewRow, nearLimitPct: number): string | undefined {
    const b = rowBand(row, nearLimitPct);
    return b === 'over' ? 'lim-row-over' : b === 'near' ? 'lim-row-near' : undefined;
}

/** The width of the meter in percent: the real value, capped at 100 (the figure beside it shows the real value). */
export function meterPercent(ratio: number | null | undefined): number {
    if (ratio === null || ratio === undefined || !Number.isFinite(ratio) || ratio <= 0) return 0;
    return Math.min(100, Math.round(ratio * 1000) / 10);
}

// ---------------------------------------------------------------- tiles

export type TileKey = 'over' | 'near' | 'preview' | 'overdue-red' | 'overdue-amber';

export interface TileDef {
    key: TileKey;
    label: string;
    /** The tooltip: the rule in words. */
    title: (nearLimitPct: number) => string;
    tone: 'red' | 'amber';
    test: (row: OverviewRow, nearLimitPct: number) => boolean;
}

export const TILES: readonly TileDef[] = [
    { key: 'over', label: 'Over limit', tone: 'red', title: () => 'Balance above the limit (utilization over 100 %)', test: (r, p) => utilizationBand(r, p) === 'over' },
    { key: 'near', label: 'Near limit', tone: 'amber', title: p => `Utilization from ${p} % up to 100 %`, test: (r, p) => utilizationBand(r, p) === 'near' },
    { key: 'preview', label: 'Preview over limit', tone: 'red', title: () => 'Balance plus open sales and frame orders above the limit', test: (r, p) => previewBand(r, p) === 'over' },
    { key: 'overdue-red', label: 'Overdue red', tone: 'red', title: () => 'Oldest open item more than the red threshold past due', test: r => r.overdueMark === 'red' },
    { key: 'overdue-amber', label: 'Overdue amber', tone: 'amber', title: () => 'Oldest open item past due, up to the red threshold', test: r => r.overdueMark === 'amber' },
];

const TILE_KEYS = new Set<string>(TILES.map(t => t.key));

export function isTileKey(v: string | null | undefined): v is TileKey {
    return typeof v === 'string' && TILE_KEYS.has(v);
}

export interface TileStat { count: number; balanceChf: number }

/** Count and balance per tile over the rows given (the page filters already applied, the tile filter not). */
export function tileStats(rows: readonly OverviewRow[], nearLimitPct: number): Record<TileKey, TileStat> {
    const out = Object.fromEntries(TILES.map(t => [t.key, { count: 0, balanceChf: 0 }])) as Record<TileKey, TileStat>;
    for (const r of rows) {
        for (const t of TILES) {
            if (!t.test(r, nearLimitPct)) continue;
            out[t.key].count += 1;
            out[t.key].balanceChf += r.balanceChf;
        }
    }
    return out;
}

// ---------------------------------------------------------------- «My customers» (E31)

export type MyCustomers =
    | { kind: 'none' }
    | { kind: 'all' }
    | { kind: 'names'; names: ReadonlySet<string> };

/** Which customers «My customers» shows; «none» = the person has no role and the switch is hidden. */
export function myCustomersOf(roles: CreditLimitCallerRoles | null | undefined): MyCustomers {
    if (!roles) return { kind: 'none' };
    if (roles.finance === true) return { kind: 'all' };
    const names = new Set<string>([...(roles.smFor ?? []), ...(roles.btmFor ?? [])]);
    return names.size > 0 ? { kind: 'names', names } : { kind: 'none' };
}

export function isMyCustomer(row: OverviewRow, mine: MyCustomers): boolean {
    if (mine.kind === 'all') return true;
    if (mine.kind === 'none') return false;
    return row.salesManagerName !== null && mine.names.has(row.salesManagerName);
}

// ---------------------------------------------------------------- search

/** Lower case, accents and umlauts reduced to the base letter, ß to ss. */
function stripped(s: string): string {
    return s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss').toLowerCase();
}

/** Lower case with the German umlauts written out (ä → ae), the rest stripped. */
function expanded(s: string): string {
    return stripped(s.normalize('NFC').toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue'));
}

/** The two spellings a text is searched in. */
export function foldVariants(s: string): [string, string] {
    return [stripped(s), expanded(s)];
}

/** The searchable text of a row: customer, number, salesperson, payment terms. */
export function searchTextOf(row: OverviewRow): [string, string] {
    const raw = [row.customerName, row.customerCode, row.salesManagerName, row.paymentTermCode].filter(Boolean).join(' \u0001 ');
    return foldVariants(raw);
}

/** The words of a query; empty = no search. */
export function searchTerms(q: string): string[] {
    return q.trim().split(/\s+/).filter(Boolean);
}

/** Whether every word occurs in the row's text, in either spelling. */
export function matchesSearch(hay: readonly [string, string], terms: readonly string[]): boolean {
    return terms.every(term => {
        const [a, b] = foldVariants(term);
        return hay[0].includes(a) || hay[1].includes(a) || hay[0].includes(b) || hay[1].includes(b);
    });
}

// ---------------------------------------------------------------- filter state and URL

export interface OverviewFilterState {
    q: string;
    sm: string | null;
    mine: boolean;
    band: TileKey | null;
}

export const EMPTY_FILTERS: OverviewFilterState = { q: '', sm: null, mine: false, band: null };

export function parseFilterParams(params: URLSearchParams): OverviewFilterState {
    const sm = params.get('sm');
    const band = params.get('band');
    return {
        q: params.get('q') ?? '',
        sm: sm !== null && sm !== '' ? sm : null,
        mine: params.get('mine') === '1',
        band: isTileKey(band) ? band : null,
    };
}

/** The URL parameters with the filter state written in; every other parameter (`tab`) stays. */
export function writeFilterParams(params: URLSearchParams, state: OverviewFilterState): URLSearchParams {
    const next = new URLSearchParams(params);
    const set = (key: string, value: string | null) => { if (value === null || value === '') next.delete(key); else next.set(key, value); };
    set('q', state.q.trim() === '' ? null : state.q);
    set('sm', state.sm);
    set('mine', state.mine ? '1' : null);
    set('band', state.band);
    return next;
}

export function activeFilterCount(state: OverviewFilterState, mineAvailable: boolean): number {
    return (state.q.trim() ? 1 : 0) + (state.sm ? 1 : 0) + (state.mine && mineAvailable ? 1 : 0) + (state.band ? 1 : 0);
}

/** The salespeople in the data, sorted; the chosen one stays in the list even when the rows no longer carry it. */
export function salesManagerOptions(rows: readonly OverviewRow[], chosen: string | null): string[] {
    const names = new Set<string>();
    for (const r of rows) if (r.salesManagerName && r.salesManagerName.trim() !== '') names.add(r.salesManagerName);
    if (chosen) names.add(chosen);
    return [...names].sort((a, b) => a.localeCompare(b, 'de-CH', { sensitivity: 'base' }));
}

/**
 * The rows the page filters leave: «My customers», salesperson and search; not the tile. `search` is the per-row text
 * (`searchTextOf`), computed once per overview by the caller.
 */
export function applyPageFilters(
    rows: readonly OverviewRow[],
    state: OverviewFilterState,
    mine: MyCustomers,
    search: (row: OverviewRow) => readonly [string, string],
): OverviewRow[] {
    const terms = searchTerms(state.q);
    const useMine = state.mine && mine.kind !== 'none';
    return rows.filter(r =>
        (!useMine || isMyCustomer(r, mine))
        && (state.sm === null || r.salesManagerName === state.sm)
        && (terms.length === 0 || matchesSearch(search(r), terms)));
}

/** The rows of a pressed tile among the filtered rows; all of them without one. */
export function applyTile(rows: readonly OverviewRow[], band: TileKey | null, nearLimitPct: number): OverviewRow[] {
    if (!band) return [...rows];
    const tile = TILES.find(t => t.key === band)!;
    return rows.filter(r => tile.test(r, nearLimitPct));
}
